// Radha — enterprise readiness reads + writes (migration 056): business
// entities (multi-GSTIN), API keys, webhook subscriptions, custom fields,
// SSO requests.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface BusinessEntity { id: string; legal_name: string; gstin: string | null; is_default: boolean; }
export function useBusinessEntities() {
  return useQuery({
    queryKey: ["business-entities"],
    queryFn: async (): Promise<BusinessEntity[]> => {
      const { data, error } = await supabase.from("business_entities").select("id, legal_name, gstin, is_default").order("legal_name");
      if (error) throw error;
      return data ?? [];
    },
  });
}
export function useAddBusinessEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { legal_name: string; gstin?: string }) => {
      const { error } = await supabase.from("business_entities").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-entities"] }),
  });
}

export interface ApiKey { id: string; label: string; key_prefix: string; scopes: string[]; created_at: string; last_used_at: string | null; revoked_at: string | null; }
export function useApiKeys() {
  return useQuery({
    queryKey: ["api-keys"],
    queryFn: async (): Promise<ApiKey[]> => {
      const { data, error } = await supabase.from("api_keys").select("id, label, key_prefix, scopes, created_at, last_used_at, revoked_at").order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (label: string): Promise<{ key: string; prefix: string }> => {
      const { data, error } = await supabase.functions.invoke("create-api-key", { body: { label } });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}
export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("api_keys").update({ revoked_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export interface WebhookSubscription { id: string; url: string; event_types: string[]; is_active: boolean; last_fired_at: string | null; last_status: number | null; }
export function useWebhookSubscriptions() {
  return useQuery({
    queryKey: ["webhook-subscriptions"],
    queryFn: async (): Promise<WebhookSubscription[]> => {
      const { data, error } = await supabase.from("webhook_subscriptions").select("id, url, event_types, is_active, last_fired_at, last_status").order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}
export function useAddWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { url: string; secret: string; event_types: string[] }) => {
      const { error } = await supabase.from("webhook_subscriptions").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-subscriptions"] }),
  });
}
export function useRemoveWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("webhook_subscriptions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-subscriptions"] }),
  });
}

export interface SsoRequest { id: string; idp_name: string; idp_type: string; status: string; created_at: string; }
export function useSsoRequests() {
  return useQuery({
    queryKey: ["sso-requests"],
    queryFn: async (): Promise<SsoRequest[]> => {
      const { data, error } = await supabase.from("sso_requests").select("id, idp_name, idp_type, status, created_at").order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}
export function useRequestSso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { idp_name: string; idp_type: string; contact_email?: string; notes?: string }) => {
      const { error } = await supabase.from("sso_requests").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sso-requests"] }),
  });
}

export interface CustomFieldDefinition { id: string; field_key: string; field_label: string; field_type: string; }
export function useCustomFieldDefinitions() {
  return useQuery({
    queryKey: ["custom-field-definitions"],
    queryFn: async (): Promise<CustomFieldDefinition[]> => {
      const { data, error } = await supabase.from("custom_field_definitions").select("id, field_key, field_label, field_type").eq("entity_type", "collection_account");
      if (error) throw error;
      return data ?? [];
    },
  });
}
export function useAddCustomFieldDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { field_key: string; field_label: string; field_type: string }) => {
      const { error } = await supabase.from("custom_field_definitions").insert({ ...input, entity_type: "collection_account" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-field-definitions"] }),
  });
}
export function useRemoveCustomFieldDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("custom_field_definitions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-field-definitions"] }),
  });
}

export interface CustomFieldValue { field_key: string; value: string | null; }
export function useCustomFieldValues(accountId: string | null) {
  return useQuery({
    queryKey: ["custom-field-values", accountId],
    queryFn: async (): Promise<CustomFieldValue[]> => {
      const { data, error } = await supabase.from("custom_field_values").select("field_key, value")
        .eq("entity_type", "collection_account").eq("entity_id", accountId as string);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!accountId,
  });
}
export function useSetCustomFieldValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entity_id, field_key, value }: { entity_id: string; field_key: string; value: string }) => {
      const { error: insErr } = await supabase.from("custom_field_values").insert({ entity_type: "collection_account", entity_id, field_key, value });
      if (!insErr) return;
      if (insErr.code !== "23505") throw insErr;
      const { error: updErr } = await supabase.from("custom_field_values").update({ value })
        .eq("entity_type", "collection_account").eq("entity_id", entity_id).eq("field_key", field_key);
      if (updErr) throw updErr;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["custom-field-values", vars.entity_id] }),
  });
}

export function enterpriseErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
